import { useEffect, useState } from 'react';
import { fmtDay, fmtDayShort, dayYmd } from '@/lib/datetime';
import type { CampaignFlight } from '@/lib/campaign-flights';

export interface FlightsEditorProps {
  /** The campaign window, `YYYY-MM-DD`. */
  startDate: string;
  endDate: string;
  /** The campaign's gross budget in whole dollars. */
  totalBudget: number;
  flights: CampaignFlight[];
  setFlights: (next: CampaignFlight[]) => void;
  /** Ids of the flights the operator has touched; adding a flight keeps them. */
  editedFlights: ReadonlySet<string>;
  setEditedFlights: (next: Set<string>) => void;
  /** The campaign has left draft, so past flights lock and the current one is floored. */
  wasLive: boolean;
  /** UTC midnight of New York's current day. */
  today: Date;
  /**
   * Each flight's gross spend in dollars, from the same published delivery
   * Reports reads (`flight_spend` on the campaign's overview). A flight absent
   * from the map, or null, reads "—": unknown, never an estimate.
   */
  flightSpend?: ReadonlyMap<string, number | null>;
  /**
   * The provider is delivering now (operational state live or partially
   * active). Only then does the current flight read "Now delivering"; a
   * published campaign that is paused, scheduled or unconfirmed does not
   * (2026-10-19). Absent, the current flight of a published campaign keeps
   * the old reading.
   */
  delivering?: boolean;
}

/** One gap between locked flights, as UTC-midnight epoch ms, both ends inclusive. */
interface DayRange {
  s: number;
  e: number;
}

/** Inclusive day count of a range. */
const rangeDays = (r: DayRange): number => Math.floor((r.e - r.s) / 864e5) + 1;

type FlightPhase = 'past' | 'current' | 'future';

export function FlightsEditor({ startDate, endDate, totalBudget, flights, setFlights, editedFlights, setEditedFlights, wasLive, today, flightSpend, delivering }: FlightsEditorProps) {
  const [addError, setAddError] = useState<string | null>(null);
  useEffect(() => {
    if (!addError) return;
    const t = setTimeout(() => setAddError(null), 5000);
    return () => clearTimeout(t);
  }, [addError]);
  const sumBudget = flights.reduce((a,f) => a + (+f.budget||0), 0);
  const remaining = totalBudget - sumBudget;
  const sorted = [...flights].sort((a,b) => new Date(a.start).getTime() - new Date(b.start).getTime());

  // Overlap detection
  const errors: Record<string, string> = {};
  for (let i = 0; i < sorted.length; i++) {
    const f = sorted[i];
    if (!f) continue;
    if (new Date(f.start) < new Date(startDate)) errors[f.id] = 'Starts before campaign start';
    if (new Date(f.end) > new Date(endDate)) errors[f.id] = 'Ends after campaign end';
    if (new Date(f.end) < new Date(f.start)) errors[f.id] = 'End before start';
    const prev = sorted[i-1];
    if (prev) {
      if (new Date(f.start).getTime() <= new Date(prev.end).getTime()) {
        errors[f.id] = `Overlaps with ${prev.name}`;
      }
    }
  }

  const addFlight = () => {
    const startTs = new Date(startDate).getTime();
    const endTs = new Date(endDate).getTime();
    if (isNaN(startTs) || isNaN(endTs) || endTs < startTs) return;

    // If any flight has been manually edited, preserve them and only redistribute
    // the unedited (auto) flights + the new one in the remaining free space.
    const lockedFlights = flights.filter(f => editedFlights.has(f.id));
    if (lockedFlights.length > 0) {
      // Compute occupied ranges by locked flights and total locked budget
      const occupied = lockedFlights
        .map(f => ({ id: f.id, s: new Date(f.start).getTime(), e: new Date(f.end).getTime(), b: +f.budget || 0 }))
        .sort((a, b) => a.s - b.s);
      const lockedBudget = occupied.reduce((acc, r) => acc + r.b, 0);
      // Build free time ranges between locked flights (1 day buffer enforced)
      const freeRanges: DayRange[] = [];
      let cursor = startTs;
      for (const r of occupied) {
        if (r.s > cursor) freeRanges.push({ s: cursor, e: r.s - 864e5 });
        cursor = r.e + 864e5;
      }
      if (cursor <= endTs) freeRanges.push({ s: cursor, e: endTs });
      const newAutoCount = flights.filter(f => !editedFlights.has(f.id)).length + 1;
      const totalFreeDays = freeRanges.reduce((a, r) => a + rangeDays(r), 0);

      if (totalFreeDays < newAutoCount) {
        setAddError('No room to add another flight. Your edited flights already fill the campaign window — shorten one of them or extend the campaign end date to free up dates.');
        return;
      }

      const remainingBudget = Math.max(0, totalBudget - lockedBudget);
      const per = Math.floor(remainingBudget / newAutoCount);
      const budgetRemainder = remainingBudget - per * newAutoCount;

      // Allocate # of flights per free range proportionally to range size,
      // ensuring each range gets at least 1 flight (if newAutoCount permits)
      // and the total matches newAutoCount.
      const rangeFlightCounts = freeRanges.map(r =>
        Math.max(1, Math.round((rangeDays(r) / totalFreeDays) * newAutoCount))
      );
      // Reconcile to exactly newAutoCount
      let allocated = rangeFlightCounts.reduce((a, b) => a + b, 0);
      while (allocated > newAutoCount) {
        // remove from the range with the most allocated flights (>1)
        const maxIdx = rangeFlightCounts.reduce((mi, v, i, a) => v > (a[mi] ?? 0) && v > 1 ? i : mi, 0);
        rangeFlightCounts[maxIdx] = (rangeFlightCounts[maxIdx] ?? 0) - 1;
        allocated--;
      }
      while (allocated < newAutoCount) {
        // add to the range with the most days per flight
        // freeRanges and rangeFlightCounts are index-aligned, so every index
        // read here is in range.
        const bestIdx = rangeFlightCounts.reduce((bi, v, i, a) => {
          const range = freeRanges[i];
          const best = freeRanges[bi];
          if (!range || !best) return bi;
          return (rangeDays(range) / v) > (rangeDays(best) / (a[bi] ?? 0)) ? i : bi;
        }, 0);
        rangeFlightCounts[bestIdx] = (rangeFlightCounts[bestIdx] ?? 0) + 1;
        allocated++;
      }

      const autoNew: DayRange[] = [];
      for (const [ri, r] of freeRanges.entries()) {
        const n = rangeFlightCounts[ri] ?? 0;
        if (n === 0) continue;
        const rDays = rangeDays(r);
        const base = Math.floor(rDays / n);
        let extra = rDays - base * n;
        let cur = r.s;
        for (let i = 0; i < n; i++) {
          const d = base + (extra > 0 ? 1 : 0);
          if (extra > 0) extra--;
          const fs = cur;
          const fe = cur + (d - 1) * 864e5;
          autoNew.push({ s: fs, e: fe });
          cur = fe + 864e5;
        }
      }

      const oldAutoFlights = flights.filter(f => !editedFlights.has(f.id));
      const merged: CampaignFlight[] = [
        ...lockedFlights,
        ...autoNew.map((slot, i) => {
          const oldAuto = oldAutoFlights[i];
          return {
            id: oldAuto?.id || `f${Date.now()}-${i}-${Math.random().toString(36).slice(2,6)}`,
            name: oldAuto?.name || '__auto__',
            start: dayYmd(slot.s),
            end: dayYmd(slot.e),
            budget: per + (i === 0 ? budgetRemainder : 0),
          };
        }),
      ];
      merged.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
      // Renumber auto flights by date order, preserving user-renamed and locked names
      let counter = 1;
      for (const f of merged) {
        const isDefaultName = /^Flight \d+$/.test(f.name) || f.name === '__auto__';
        const isLocked = editedFlights.has(f.id);
        if (!isLocked && isDefaultName) {
          // find next available number not used by a locked flight
          while (merged.some(x => editedFlights.has(x.id) && x.name === `Flight ${counter}`)) counter++;
          f.name = `Flight ${counter++}`;
        }
      }
      setFlights(merged);
      return;
    }

    // No locked flights: full even redistribution including the new one
    const n = flights.length + 1;
    const totalDays = Math.floor((endTs - startTs) / 864e5) + 1;
    if (totalDays < n) {
      setAddError(`Not enough days in the campaign window to fit ${n} non-overlapping flights. Extend the campaign end date.`);
      return;
    }
    setAddError(null);
    const baseDays = Math.floor(totalDays / n);
    const extra = totalDays - baseDays * n;
    const per = Math.floor(totalBudget / n);
    const budgetRemainder = totalBudget - per * n;
    const newFlights: CampaignFlight[] = [];
    let cursor = startTs;
    for (let i = 0; i < n; i++) {
      const days = baseDays + (i < extra ? 1 : 0);
      const s = cursor;
      const e = cursor + (days - 1) * 864e5;
      const existing = flights[i];
      newFlights.push({
        id: existing?.id || `f${Date.now()}-${i}`,
        name: existing?.name || `Flight ${i + 1}`,
        start: dayYmd(s),
        end: dayYmd(e),
        budget: per + (i === 0 ? budgetRemainder : 0),
      });
      cursor = e + 864e5;
    }
    setFlights(newFlights);
  };

  const markEdited = (id: string) => {
    if (!editedFlights.has(id)) {
      const next = new Set(editedFlights);
      next.add(id);
      setEditedFlights(next);
    }
  };

  const updateFlightAndMark = (id: string, patch: Partial<CampaignFlight>) => {
    markEdited(id);
    updateFlight(id, patch);
  };

  const removeFlightAndForget = (id: string) => {
    const next = new Set(editedFlights);
    next.delete(id);
    setEditedFlights(next);
    removeFlight(id);
  };

  const updateFlight = (id: string, patch: Partial<CampaignFlight>) => {
    setFlights(flights.map(f => f.id === id ? { ...f, ...patch } : f));
  };

  const removeFlight = (id: string) => {
    setFlights(flights.filter(f => f.id !== id));
  };

  const distributeEvenly = () => {
    if (flights.length === 0) return;
    const per = Math.floor(totalBudget / flights.length);
    const remainder = totalBudget - per * flights.length;
    setFlights(flights.map((f, i) => ({ ...f, budget: per + (i === 0 ? remainder : 0) })));
  };

  // Visual timeline — dates are INCLUSIVE so the total span is end-start + 1 day
  const startTs = new Date(startDate).getTime();
  const endTs = new Date(endDate).getTime();
  const totalSpan = Math.max(1, endTs - startTs + 864e5);

  return (
    <div className="flights-block">
      <div className="flights-hd flex between items-end mb-2" style={{gap:10}}>
        <div>
          <div className="field-lbl" style={{margin:0}}>Flights · {flights.length}</div>
          <div className="muted" style={{fontSize:11,marginTop:2}}>Non-overlapping budget pulses within the campaign window. Sum must equal total budget.</div>
        </div>
        <div className="flex gap-2 items-center">
          <span className="mono" style={{fontSize:11,color: remaining === 0 ? 'var(--good)' : 'var(--bad)'}}>
            {remaining === 0 ? '✓ Balanced' : remaining > 0 ? `${'$' + remaining.toLocaleString()} unallocated` : `${'$' + Math.abs(remaining).toLocaleString()} over budget`}
          </span>
          <button className="btn btn-sm btn-ghost" onClick={distributeEvenly} disabled={flights.length === 0}>Split evenly</button>
          <button className="btn btn-sm" onClick={addFlight}>+ Flight</button>
        </div>
      </div>

      {/* Timeline visualization */}
      {flights.length > 0 && (
        <div className="flight-timeline">
          <div className="flight-timeline-bar">
            {sorted.map((f, i) => {
              const fs = new Date(f.start).getTime();
              const fe = new Date(f.end).getTime();
              const left = ((fs - startTs) / totalSpan) * 100;
              const width = ((fe - fs + 864e5) / totalSpan) * 100;
              return (
                <div key={f.id} className={`flight-bar ${errors[f.id] ? 'flight-bar-err' : ''}`}
                  style={{left: `${left}%`, width: `${width}%`, background: `oklch(${0.65 - i*0.05} 0.13 ${(i * 60 + 30) % 360})`}}
                  title={`${f.name} · ${fmtDay(f.start)} → ${fmtDay(f.end)} · $${(+f.budget||0).toLocaleString()}`}>
                  <span>{f.name}</span>
                </div>
              );
            })}
          </div>
          <div className="flight-timeline-axis">
            <span>{fmtDayShort(startDate)}</span>
            <span>{fmtDayShort(endDate)}</span>
          </div>
        </div>
      )}

      <div className="flights-list">
        {sorted.map((f, i) => {
          const isEdited = editedFlights.has(f.id);
          const color = `oklch(${0.65 - i*0.05} 0.13 ${(i * 60 + 30) % 360})`;

          // ── Per-flight status (past / current / future) ─────────────
          // Only matters when the campaign has gone live. Until launch every
          // flight is fully editable.
          // f.start/f.end are YYYY-MM-DD → new Date() parses them at UTC midnight;
          // `today` is UTC-midnight of NY's day, so these compare as calendar days.
          const fs = new Date(f.start);
          const fe = new Date(f.end);
          const tCmp = today || new Date();
          let phase: FlightPhase = 'future';
          if (fe < tCmp) phase = 'past';
          else if (fs <= tCmp && tCmp <= fe) phase = 'current';

          // What the flight has really spent (null: unknown), as Reports reads it.
          const flightBudget = +f.budget || 0;
          const flightSpent = flightSpend?.get(f.id) ?? null;
          const spentLabel = flightSpent === null
            ? '—'
            : `$${flightSpent.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

          // ── PAST flight — read-only card ──────────────────────────────
          // It ended; whether it delivered is what Reports says it spent. Until
          // 2026-10-19 every past flight read "Delivered", spend or not.
          if (wasLive && phase === 'past') {
            return (
              <div key={f.id} className="flight-row-past">
                <span className="fp-stripe" style={{background: color}}/>
                <div className="fp-name">
                  {f.name}
                  <span style={{display:'block',fontFamily:'var(--font-mono)',fontSize:10,letterSpacing:'.06em',color:'var(--ink-3)',marginTop:2,textTransform:'uppercase'}}>Flight ended</span>
                </div>
                <div className="fp-dates">
                  {fmtDayShort(f.start)} → {fmtDay(f.end)}
                </div>
                <div className="fp-spent">
                  <b title={flightSpent === null ? 'Delivery for this flight is not fully reported yet' : undefined}>{spentLabel}</b>
                  <div className="muted">of ${flightBudget.toLocaleString()}</div>
                </div>
                {flightSpent !== null && flightSpent > 0 && <span className="fp-badge">Delivered</span>}
              </div>
            );
          }

          // ── UPCOMING flight of a published campaign — read-only card ──────
          // A published plan's schedule is locked, so a flight that has not started
          // (one an Extend flight just added, 2026-10-21) says when it starts instead
          // of wearing inputs nobody can use.
          if (wasLive && phase === 'future') {
            return (
              <div key={f.id} className="flight-row-past flight-row-upcoming">
                <span className="fp-stripe" style={{background: color}}/>
                <div className="fp-name">
                  {f.name}
                  <span className="fp-phase">Starts {fmtDay(f.start)}</span>
                </div>
                <div className="fp-dates">
                  {fmtDayShort(f.start)} → {fmtDay(f.end)}
                </div>
                <div className="fp-spent">
                  <b>${flightBudget.toLocaleString()}</b>
                  <div className="muted">budget</div>
                </div>
                <span className="fp-badge">Upcoming</span>
              </div>
            );
          }

          // ── CURRENT or FUTURE flight — interactive row with constraints
          const isCurrent = wasLive && phase === 'current';
          // Future flights' start must be ≥ today and ≥ prior flight's end + 1 day
          const prior = sorted[i - 1];
          const minStart = wasLive
            ? (() => {
                let m = new Date(today);
                if (prior) {
                  const pe = new Date(prior.end);
                  pe.setUTCDate(pe.getUTCDate() + 1);
                  if (pe > m) m = pe;
                }
                return dayYmd(m);
              })()
            : startDate;

          return (
            <div key={f.id} className={`flight-row ${errors[f.id] ? 'flight-row-err' : ''} ${isCurrent ? 'flight-row-current' : ''}`} title={isEdited ? 'Edited — preserved when adding more flights' : 'Auto-distributed'} style={isCurrent ? {marginTop: 14} : undefined}>
              <span style={{width:6, height:'24px', borderRadius:2, background: color, flexShrink:0, opacity: isEdited ? 1 : 0.55, boxShadow: isEdited ? `0 0 0 1px ${color} inset, 0 0 0 2px var(--bg)` : 'none'}} title={isEdited ? 'Locked — both dates and budget will be preserved when adding/redistributing flights' : 'Auto — redistributes when adding flights'}/>
              <input className="input" style={{flex:1.4, minWidth:0}} aria-label={`Flight ${i + 1} name`} value={f.name} onChange={e=>updateFlight(f.id, {name: e.target.value})}/>
              <input
                type="date"
                className={`input mono${isCurrent ? ' input-locked' : ''}`}
                style={{flex:1, minWidth:0}}
                aria-label={`Flight ${i + 1} start date`}
                value={f.start}
                min={isCurrent ? f.start : minStart}
                max={isCurrent ? f.start : (f.end || endDate)}
                readOnly={isCurrent}
                onChange={e => !isCurrent && updateFlightAndMark(f.id, {start: e.target.value})}
                title={isCurrent ? 'Start date locked — flight is currently running' : ''}
              />
              <input type="date" className="input mono" style={{flex:1, minWidth:0}} aria-label={`Flight ${i + 1} end date`} value={f.end} min={f.start || startDate} max={endDate} onChange={e=>updateFlightAndMark(f.id, {end: e.target.value})}/>
              <input
                className="input mono"
                style={{flex:1, minWidth:0}}
                aria-label={`Flight ${i + 1} budget`}
                value={'$' + flightBudget.toLocaleString()}
                onChange={e => {
                  const n = parseInt(e.target.value.replace(/[^\d]/g,''));
                  updateFlightAndMark(f.id, {budget: isNaN(n) ? 0 : n});
                }}
              />
              <button className="btn btn-sm btn-ghost" style={{padding:'6px 8px', visibility: isCurrent ? 'hidden' : 'visible'}} onClick={()=>removeFlightAndForget(f.id)} title={isCurrent ? 'Cannot remove a currently-running flight' : 'Remove flight'} disabled={isCurrent}>×</button>
              {isCurrent && (
                <div className="flight-row-live-strip">
                  {(delivering ?? true)
                    ? <span className="live-tag">Now delivering</span>
                    : <span className="mono" style={{color:'var(--ink-3)',textTransform:'uppercase',letterSpacing:'.06em'}}>Current flight</span>}
                  <span>·</span>
                  <span>Spent <b title={flightSpent === null ? 'Delivery for this flight is not fully reported yet' : undefined}>{spentLabel}</b> of ${flightBudget.toLocaleString()}</span>
                </div>
              )}
            </div>
          );
        })}
        {flights.length === 0 && (
          <div className="muted" style={{padding:'12px',fontSize:11.5,textAlign:'center',border:'1px dashed var(--line)',borderRadius:'var(--r-sm)'}}>
            No flights yet. Add at least one flight to allocate budget across the campaign window.
          </div>
        )}
      </div>

      {Object.keys(errors).length > 0 && (
        <div className="asset-err" style={{marginTop:8}}>
          <span style={{flexShrink:0}}>⚠</span>
          <span style={{flex:1}}>
            {Object.entries(errors).map(([id, msg]) => {
              const f = flights.find(x => x.id === id);
              return `${f?.name || id}: ${msg}`;
            }).join(' · ')}
          </span>
        </div>
      )}
      {addError && (
        <div className="asset-err" style={{marginTop:8}}>
          <span style={{flexShrink:0}}>⚠</span>
          <span style={{flex:1}}>{addError}</span>
          <button className="btn btn-sm btn-ghost" style={{padding:'2px 6px'}} onClick={()=>setAddError(null)} title="Dismiss">×</button>
        </div>
      )}
    </div>
  );
}
